import { useState } from "react";
import {
  GeneralDetailsTab,
  AvailabilityTab,
  IngredientsTab,
  ExecutionStepsTab,
  StorageContainersTab,
  ShelfLifeStorageTab,
  LabelingTab,
  CostFinancialsTab,
} from "@/components/recipes/detail";
import type { Recipe } from "@/types/recipe";
import { ccpStepCount } from "@/lib/recipes/ccp";
import { CcpWarningBadge } from "./ccp";
import { RecipeMetadataStrip } from "./RecipeMetadataStrip";
import { RecipeTabNav, type RecipeTab } from "./RecipeTabNav";

interface RecipeDetailViewProps {
  recipe: Recipe;
  onEditTab?: (tab: RecipeTab) => void;
  /**
   * Containers and tools are managed from this screen rather than through the
   * editor: they are a list you add to while the recipe is in front of you, and
   * each row saves on its own. Off by default so the editor's preview stays a
   * preview.
   */
  canManage?: boolean;
  contentPaddingBottom?: string;
}

export function RecipeDetailView({
  recipe,
  onEditTab,
  canManage = false,
  contentPaddingBottom = "pb-10",
}: RecipeDetailViewProps) {
  const [activeTab, setActiveTab] = useState<RecipeTab>("general");

  return (
    <>
      <div className="flex flex-col gap-5 px-3">
        <CcpWarningBadge count={ccpStepCount(recipe.instructions)} className="self-start" />
        <RecipeMetadataStrip recipe={recipe} />
        <RecipeTabNav activeTab={activeTab} onTabChange={setActiveTab} showStatusIcons={false} />
      </div>

      <div className={`px-6 ${contentPaddingBottom}`} role="tabpanel">
        {activeTab === "general" && (
          <GeneralDetailsTab
            recipe={recipe}
            onEdit={onEditTab ? () => onEditTab("general") : undefined}
          />
        )}
        {activeTab === "availability" && (
          <AvailabilityTab
            recipe={recipe}
            readOnly
            onEdit={onEditTab ? () => onEditTab("availability") : undefined}
          />
        )}
        {activeTab === "ingredients" && (
          <IngredientsTab
            recipe={recipe}
            onEdit={onEditTab ? () => onEditTab("ingredients") : undefined}
            canAdd={!!onEditTab}
          />
        )}
        {activeTab === "execution" && (
          <ExecutionStepsTab
            recipe={recipe}
            onEdit={onEditTab ? () => onEditTab("execution") : undefined}
          />
        )}
        {activeTab === "containers" && (
          <StorageContainersTab recipe={recipe} canManage={canManage} />
        )}
        {activeTab === "shelfLife" && <ShelfLifeStorageTab recipe={recipe} canManage={false} />}
        {activeTab === "labeling" && <LabelingTab recipe={recipe} canManage={false} />}
        {activeTab === "cost" && <CostFinancialsTab recipe={recipe} readOnly />}
      </div>
    </>
  );
}
